// TurniertagPage.jsx — Turniertag vorbereiten und starten (/turniertag/:turnierId, 18.10.2026)
//
// Ersetzt die WettkampftagChecklist: deren Haken lagen nur im Browser und
// wussten nichts über das Turnier. Hier misst der Server
// (GET /api/turniertag/:id/stand, services/vorbereitungsstand.turniertagStand).
// Dazu der Schalter „Kämpfe nach dem Einwiegen automatisch erzeugen“
// (turniere.brackets_nach_einwaage, POST /api/einwaage/:id/automatik), der
// Knopf „Turniertag starten“ (POST /api/turniertag/:id/starten) und die
// Gerätekarte zum Drucken: je Rolle und Matte ein QR-Code mit Adresse.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { TURNIER_STATUS, statusText } from '../lib/turnierStatus';
import './TurniertagPage.css';

const STATUS_TEXT = { ok: 'in Ordnung', warnung: 'prüfen', fehlt: 'fehlt' };

// Wohin ein Punkt führt (Dashboard-Bereich oder eigene Seite)
const ZIEL = {
  matten: '/dashboard/kampfflaechen', pools: '/dashboard/brackets', kaempfe: '/dashboard/brackets',
  formen: '/dashboard/formen', zeitplan: '/dashboard/zeitplan', einzelstarter: '/dashboard/einzelstarter',
  kr_konten: '/dashboard/staff-management', kr_matten: '/dashboard/staff-management',
  waage: '/dashboard/staff-management', registrierung: '/dashboard/staff-management',
};

const kopf = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}`, 'Content-Type': 'application/json' });

async function antwort(r) {
  const d = await r.json().catch(() => ({}));
  if (r.status === 401) throw new Error('Nicht angemeldet. Bitte neu anmelden.');
  if (!r.ok || d.success === false) throw new Error(d.error || d.message || `Fehler ${r.status}`);
  return d;
}

/** Alle Geräte des Turniertags: [{ gruppe, titel, sub, pfad }]
 *  hallenSchluessel: EIN Schlüssel für alle Hallenbildschirme (21.10.2026,
 *  Leitstelle „Hallenschlüssel“); ohne ihn keine Leinwände und keine Fotos. */
function geraeteListe(turnierId, matten, hallenSchluessel) {
  const k = hallenSchluessel ? `&k=${hallenSchluessel}` : '';
  const liste = [];
  for (const m of matten) {
    liste.push({ gruppe: m.name, titel: 'Kampfrichter', sub: 'Tablet, Anmeldung mit Personalkonto (Kampfrichter)', pfad: `/judge/${m.nummer}` });
    liste.push({ gruppe: m.name, titel: 'Mattenschirm', sub: hallenSchluessel ? 'Bildschirm an der Matte, mit Fotos' : 'Bildschirm an der Matte', pfad: `/display/${m.nummer}?t=${turnierId}${k}` });
    if (hallenSchluessel) {
      liste.push({ gruppe: m.name, titel: 'Leinwand Formen', sub: 'Beamer an der Matte', pfad: `/formen-display?t=${turnierId}&m=${m.nummer}${k}` });
    }
  }
  liste.push({ gruppe: 'Halle', titel: 'Aufruf-Hallenschirm', sub: 'Jetzt und als Nächstes je Matte', pfad: `/aufruf/${turnierId}` });
  liste.push({ gruppe: 'Halle', titel: 'Aufruf steuern', sub: 'Kategorien an die Matten rufen', pfad: `/aufruf-control/${turnierId}` });
  liste.push({ gruppe: 'Halle', titel: 'Ansage-Station', sub: 'Rechner an der Lautsprecheranlage', pfad: `/ansage/${turnierId}` });
  liste.push({ gruppe: 'Halle', titel: 'Siegerehrung-Bildschirm', sub: hallenSchluessel ? 'Podium für Beamer oder TV, mit Fotos' : 'Podium für Beamer oder TV', pfad: `/siegerehrung-display/${turnierId}${hallenSchluessel ? `?k=${hallenSchluessel}` : ''}` });
  if (hallenSchluessel) {
    liste.push({ gruppe: 'Halle', titel: 'Leinwand Sieger', sub: 'Sieger nach dem Kampf', pfad: `/winner-display?t=${turnierId}${k}` });
    liste.push({ gruppe: 'Halle', titel: 'Leinwand Bruchtest', sub: 'Bruchtest-Wertung', pfad: `/bruchtest-display?t=${turnierId}${k}` });
    liste.push({ gruppe: 'Halle', titel: 'Leinwand Rumble', sub: 'Rumble-Sieger', pfad: `/rumble-sieger-display?t=${turnierId}${k}` });
  }
  liste.push({ gruppe: 'Halle', titel: 'Zuschauer', sub: 'Für Eltern und Trainer am Handy', pfad: `/zuschauer/${turnierId}` });
  liste.push({ gruppe: 'Personal', titel: 'Check-in (Empfang)', sub: 'Personalkonto mit Rolle Registrierung', pfad: '/reg' });
  liste.push({ gruppe: 'Personal', titel: 'Waage', sub: 'Personalkonto mit Rolle Waage', pfad: '/einwaage-staff' });
  liste.push({ gruppe: 'Personal', titel: 'Leitstelle', sub: 'Turnierleitung, alle Matten', pfad: `/leitstelle/${turnierId}` });
  liste.push({ gruppe: 'Personal', titel: 'Anmelden', sub: 'Eine Anmeldeseite für alle Konten', pfad: '/anmelden' });
  return liste;
}

function Geraetekarte({ turnierId, turnierName, matten, hallenSchluessel }) {
  const [bilder, setBilder] = useState({});
  const liste = geraeteListe(turnierId, matten, hallenSchluessel);
  const schluessel = liste.map((g) => g.pfad).join('|');

  useEffect(() => {
    let ab = false;
    (async () => {
      const neu = {};
      for (const g of geraeteListe(turnierId, matten, hallenSchluessel)) {
        // Inhalt ist nur die Adresse; Fehlerkorrektur M wie im KampfrichterManager
        neu[g.pfad] = await QRCode.toDataURL(`${window.location.origin}${g.pfad}`, { margin: 1, width: 240, errorCorrectionLevel: 'M' });
      }
      if (!ab) setBilder(neu);
    })().catch(() => {});
    return () => { ab = true; };
    // schluessel fasst die Liste zusammen; matten ändert sich nur mit ihr
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel]);

  const gruppen = [...new Set(liste.map((g) => g.gruppe))];
  return (
    <section className="tt-karte" aria-label="Gerätekarte">
      <div className="tt-karte-kopf">
        <h2>Gerätekarte</h2>
        <span>{turnierName}</span>
      </div>
      {gruppen.map((gr) => (
        <div key={gr} className={`tt-karte-gruppe${liste.filter((g) => g.gruppe === gr).length > 3 ? ' tt-karte-gruppe--breit' : ''}`}>
          <h3>{gr}</h3>
          <div className="tt-karte-gitter">
            {liste.filter((g) => g.gruppe === gr).map((g) => (
              <figure key={g.pfad} className="tt-qr">
                {bilder[g.pfad] ? <img src={bilder[g.pfad]} alt={`QR-Code ${g.titel}`} /> : <div className="tt-qr-platz" />}
                <figcaption>
                  <b>{g.titel}</b>
                  <span>{g.sub}</span>
                  <code>{window.location.host}{g.pfad}</code>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

export default function TurniertagPage() {
  const { turnierId } = useParams();
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState('');
  const [meldung, setMeldung] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [hallenSchluessel, setHallenSchluessel] = useState(null);

  const laden = useCallback(async () => {
    try {
      const x = await antwort(await fetch(`/api/turniertag/${turnierId}/stand`, { headers: kopf(), cache: 'no-store' }));
      setD(x);
      setFehler('');
    } catch (e) {
      setFehler(e.message === 'Failed to fetch' ? 'Server nicht erreichbar.' : e.message);
    }
  }, [turnierId]);

  useEffect(() => { laden(); }, [laden]);
  // EIN Hallenschlüssel für alle Bildschirme (21.10.2026): Mattenschirme,
  // Leinwände, Siegerehrung. Erneuert wird er in der Leitstelle. Geholt über
  // die Leinwand-Route (nur Kampfrichter, Schreiber, Turnierleitung, Admin), die ihn bei Bedarf anlegt; fehlt er,
  // bleibt die Karte ohne Leinwände und ohne Fotos, und das steht da.
  const [schluesselFehler, setSchluesselFehler] = useState('');
  useEffect(() => {
    fetch(`/api/leinwand/${turnierId}/schluessel`, { headers: kopf(), cache: 'no-store' })
      .then(async (r) => {
        const x = await r.json().catch(() => ({}));
        if (!r.ok || !x.schluessel) throw new Error(x.error || `Fehler ${r.status}`);
        setHallenSchluessel(x.schluessel);
        setSchluesselFehler('');
      })
      .catch((e) => { setHallenSchluessel(null); setSchluesselFehler(e.message); });
  }, [turnierId]);

  const automatikSetzen = async (aktiv) => {
    setLaeuft(true); setMeldung('');
    try {
      const x = await antwort(await fetch(`/api/einwaage/${turnierId}/automatik`, { method: 'POST', headers: kopf(), body: JSON.stringify({ aktiv }) }));
      const n = (x.erzeugt || []).length;
      setMeldung(aktiv ? `Automatik eingeschaltet.${n ? ` ${n} fertig gewogene Divisionen haben jetzt Kämpfe.` : ''}` : 'Automatik ausgeschaltet.');
      await laden();
    } catch (e) {
      setFehler(`Automatik: ${e.message}`);
    } finally { setLaeuft(false); }
  };

  const starten = async () => {
    const offen = d.punkte.filter((p) => p.status === 'fehlt').length;
    const frage = `Turniertag jetzt starten? Der Status geht auf „${statusText(TURNIER_STATUS.LAUFEND)}“.`
      + (offen ? `\n\n${offen} Punkt(e) fehlen noch.` : '')
      + (!d.turnier.automatik_einwaage ? '\n\nHinweis: Die Automatik „Kämpfe nach dem Einwiegen erzeugen“ ist aus. Kämpfe müssen dann von Hand erzeugt werden.' : '');
    if (!window.confirm(frage)) return;
    setLaeuft(true); setMeldung('');
    try {
      await antwort(await fetch(`/api/turniertag/${turnierId}/starten`, { method: 'POST', headers: kopf(), body: JSON.stringify({}) }));
      setMeldung('Turniertag gestartet.');
      await laden();
    } catch (e) {
      setFehler(`Starten: ${e.message}`);
    } finally { setLaeuft(false); }
  };

  return (
    <div className="tt">
      <header className="tt-kopf">
        <div>
          <h1>Turniertag vorbereiten</h1>
          <div className="tt-sub">{d?.turnier?.name || 'wird geladen …'}{d ? ` · Status: ${d.turnier.status_text || statusText(d.turnier.status)}` : ''}</div>
        </div>
        <nav className="tt-kopf-links">
          <button type="button" onClick={laden}>Neu prüfen</button>
          <button type="button" onClick={() => window.print()}>Gerätekarte drucken</button>
          <Link to={`/leitstelle/${turnierId}`}>Leitstelle</Link>
          <Link to="/dashboard">Dashboard</Link>
        </nav>
      </header>

      {fehler && <div className="tt-banner tt-banner--fehler" role="alert">{fehler}</div>}
      {meldung && <div className="tt-banner" role="status">{meldung}</div>}

      {d && (
        <>
          <div className="tt-start">
            <label className={`tt-schalter${d.turnier.automatik_einwaage ? ' tt-schalter--an' : ''}`}>
              <input type="checkbox" checked={d.turnier.automatik_einwaage} disabled={laeuft}
                onChange={(e) => automatikSetzen(e.target.checked)} />
              <span className="tt-schalter-knopf" aria-hidden="true" />
              <span>
                <b>Kämpfe nach dem Einwiegen automatisch erzeugen</b>
                <small>Sobald jede Person einer Division gewogen ist (bestanden oder nicht erschienen), entstehen die Kämpfe von selbst.</small>
              </span>
            </label>
            <div className="tt-startknopf">
              {d.turnier.laeuft
                ? <div className="tt-laeuft">Der Turniertag läuft.</div>
                : <button type="button" className="tt-primaer" onClick={starten} disabled={laeuft}>Turniertag starten</button>}
              <small>{d.stand.ok} von {d.stand.gesamt} Punkten in Ordnung</small>
            </div>
          </div>

          <section className="tt-liste" aria-label="Prüfung">
            {d.punkte.map((p) => (
              <div key={p.id} className={`tt-punkt tt-punkt--${p.status}`}>
                <span className="tt-punkt-marke">{STATUS_TEXT[p.status]}</span>
                <div className="tt-punkt-text">
                  <b>{p.titel}</b>
                  <span>{p.text}</span>
                </div>
                {p.status !== 'ok' && (ZIEL[p.id]
                  ? <Link to={ZIEL[p.id]} className="tt-wo">{p.wo || 'Öffnen'}</Link>
                  : p.wo && <span className="tt-wo tt-wo--text">{p.wo}</span>)}
              </div>
            ))}
          </section>

          {schluesselFehler && <div className="tt-banner tt-banner--fehler" role="alert">Hallenschlüssel nicht ladbar ({schluesselFehler}): die Gerätekarte zeigt keine Leinwände.</div>}
          <Geraetekarte turnierId={turnierId} turnierName={d.turnier.name} matten={d.matten} hallenSchluessel={hallenSchluessel} />
        </>
      )}
    </div>
  );
}
